import { useLingui } from "@lingui/react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

export const ArabicFontProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useLingui();
  const isArabic = i18n.locale.startsWith("ar");

  useEffect(() => {
    // Check if the current language is Arabic
    if (isArabic) {
      // Add Arabic font stylesheet if it's not already added
      if (!document.querySelector("#arabic-font-stylesheet")) {
        const fontLink = document.createElement("link");
        fontLink.id = "arabic-font-stylesheet";
        fontLink.rel = "stylesheet";
        fontLink.href = "/styles/arabic-fonts.css";
        document.head.append(fontLink);

        // Add RTL utilities stylesheet if it's not already added
        if (!document.querySelector("#rtl-utilities-stylesheet")) {
          const rtlLink = document.createElement("link");
          rtlLink.id = "rtl-utilities-stylesheet";
          rtlLink.rel = "stylesheet";
          rtlLink.href = "/styles/rtl-utilities.css";
          document.head.append(rtlLink);
        }

        // Add RTL direction
        document.documentElement.dir = "rtl";

        // Add RTL class for Tailwind to work properly
        document.documentElement.classList.add("rtl");

        // Force all text to be right-aligned
        document.body.style.textAlign = "right";
        document.body.style.direction = "rtl";

        // Force all textareas to be right-aligned with JavaScript
        const enforceTextareaRTL = () => {
          const textareas = Array.from(document.querySelectorAll("textarea"));
          for (const textarea of textareas) {
            textarea.style.textAlign = "right";
            textarea.style.direction = "rtl";
            textarea.style.unicodeBidi = "embed";
          }
        };

        // Run immediately and on DOM changes
        enforceTextareaRTL();
        const observer = new MutationObserver(enforceTextareaRTL);
        observer.observe(document.body, { childList: true, subtree: true });

        // Store observer for cleanup
        (window as any).__rtlObserver = observer;
      }
    } else {
      // Remove Arabic font stylesheet if language is not Arabic
      const stylesheet = document.querySelector("#arabic-font-stylesheet");
      if (stylesheet) {
        stylesheet.remove();
      }

      // Remove RTL utilities stylesheet if language is not Arabic
      const rtlStylesheet = document.querySelector("#rtl-utilities-stylesheet");
      if (rtlStylesheet) {
        rtlStylesheet.remove();
      }

      // Reset direction to LTR if not Arabic
      document.documentElement.dir = "ltr";

      // Remove RTL class
      document.documentElement.classList.remove("rtl");

      // Reset text alignment to left
      document.body.style.textAlign = "left";
      document.body.style.direction = "ltr";

      // Clean up RTL observer
      if ((window as any).__rtlObserver) {
        (window as any).__rtlObserver.disconnect();
        delete (window as any).__rtlObserver;
      }

      // Reset all textareas to LTR
      const textareas = Array.from(document.querySelectorAll("textarea"));
      for (const textarea of textareas) {
        textarea.style.textAlign = "left";
        textarea.style.direction = "ltr";
      }
    }
  }, [i18n.locale, isArabic]);

  return (
    <>
      <Helmet>
        <html lang={i18n.locale} />
      </Helmet>
      {children}
    </>
  );
};
